import { useState } from 'react';
import {
  AlertTriangle,
  XCircle,
  Link2,
  Layers,
  Network,
  Lightbulb,
  ChevronDown,
  Info,
} from 'lucide-react';
import { getScoreColor } from '@/lib/utils';
import { SitemapTree } from './UIComponents';
import { DepthDistribution } from '@/components/visualizations/DepthDistribution';
import type { SiteArchitecture, SitemapNode, PageSeoData } from '@/types/audit';

interface SiteArchitecturePanelProps {
  architecture: SiteArchitecture;
  sitemap?: SitemapNode;
  pages?: PageSeoData[];
}

export function SiteArchitecturePanel({ architecture, sitemap }: SiteArchitecturePanelProps) {
  const { score, structure, linkAnalysis, navigationStructure, issues, recommendations } = architecture;

  const criticalIssues = issues.filter((i) => i.severity === 'critical' || i.severity === 'high');
  const mediumIssues = issues.filter((i) => i.severity === 'medium');
  const lowIssues = issues.filter((i) => i.severity === 'low');

  return (
    <div className="space-y-8">
      {/* Score Overview */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium">Site Architecture Analysis</h3>
          <p className="text-sm text-muted-foreground">
            Internal linking structure and crawlability
          </p>
        </div>
        <div className="text-right">
          <p className={`text-3xl font-light ${getScoreColor(score)}`}>{score}</p>
          <p className="text-xs text-muted-foreground">architecture score</p>
        </div>
      </div>

      {/* Architecture explainer */}
      <ContextNote text="Site architecture is how your pages are connected to each other — it determines whether Google can find all your content and whether visitors can easily navigate to your menu, events, and specials." />

      {/* Critical Issues Banner */}
      {criticalIssues.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-400">
                {criticalIssues.length} critical architecture issue{criticalIssues.length > 1 ? 's' : ''} found
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                {criticalIssues.map((issue, i) => (
                  <li key={i}>{issue.message}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Structure Overview Stats */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-muted-foreground" />
          <h4 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
            Structure Overview
          </h4>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatBox label="Pages Crawled" value={structure.totalPages} />
          <StatBox
            label="Max Depth"
            value={structure.maxDepth}
            status={structure.maxDepth <= 3 ? 'good' : structure.maxDepth <= 4 ? 'warning' : 'bad'}
          />
          <StatBox label="Avg Depth" value={structure.avgDepth.toFixed(1)} />
          <StatBox
            label="Avg Links/Page"
            value={linkAnalysis.avgLinksPerPage}
            status={linkAnalysis.avgLinksPerPage >= 5 ? 'good' : linkAnalysis.avgLinksPerPage >= 3 ? 'warning' : 'bad'}
          />
        </div>

        {/* Depth Distribution Chart */}
        {Object.keys(structure.pagesAtDepth).length > 0 && (
          <div className="bg-secondary/30 rounded-lg p-4">
            <h5 className="text-sm font-medium mb-3">Pages by Depth Level</h5>
            <DepthDistribution pagesAtDepth={structure.pagesAtDepth} />
          </div>
        )}
      </section>

      {/* Link Analysis */}
      <section className="space-y-4">
        <div className="flex items-center gap-2">
          <Link2 className="h-5 w-5 text-muted-foreground" />
          <h4 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
            Link Analysis
          </h4>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-secondary/30 rounded-lg p-4">
            <p className="text-xs text-muted-foreground mb-1">Internal Links</p>
            <p className="text-2xl font-light">{linkAnalysis.totalInternalLinks}</p>
          </div>
          <div className="bg-secondary/30 rounded-lg p-4">
            <p className="text-xs text-muted-foreground mb-1">External Links</p>
            <p className="text-2xl font-light">{linkAnalysis.totalExternalLinks}</p>
          </div>
        </div>

        {/* Link Types Breakdown */}
        {Object.keys(linkAnalysis.linksByType).length > 0 && (
          <div className="bg-secondary/30 rounded-lg p-4">
            <h5 className="text-sm font-medium mb-3">Links by Type</h5>
            <div className="flex flex-wrap gap-2">
              {Object.entries(linkAnalysis.linksByType).map(([type, count]) => (
                <span
                  key={type}
                  className="px-3 py-1 bg-secondary rounded text-xs"
                >
                  {type}: {count}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Hub Pages */}
        {linkAnalysis.hubPages.length > 0 && (
          <div className="bg-secondary/30 rounded-lg p-4">
            <h5 className="text-sm font-medium mb-3">Most Linked Pages (Hub Pages)</h5>
            <div className="space-y-2">
              {linkAnalysis.hubPages.slice(0, 5).map((hub, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground truncate">{hub.url}</span>
                  <span className="text-green-400 font-medium">{hub.inboundCount} links</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Hub Pages context */}
        {linkAnalysis.hubPages.length > 0 && (
          <ContextNote text="These are your most important pages — they get the most internal links and search engine attention." />
        )}

        {/* Orphan Pages */}
        {linkAnalysis.orphanPages.length > 0 && (
          <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <XCircle className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-orange-400">
                  {linkAnalysis.orphanPages.length} Orphan Page{linkAnalysis.orphanPages.length > 1 ? 's' : ''} (No Inbound Links)
                </p>
                <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                  {linkAnalysis.orphanPages.slice(0, 5).map((page, i) => (
                    <li key={i}>{page}</li>
                  ))}
                  {linkAnalysis.orphanPages.length > 5 && (
                    <li className="italic">...and {linkAnalysis.orphanPages.length - 5} more</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Orphan Pages context */}
        {linkAnalysis.orphanPages.length > 0 && (
          <ContextNote text="These pages exist but no other page links to them. Google may not know they exist, and visitors can't navigate to them." />
        )}

        {/* Dead End Pages */}
        {linkAnalysis.deadEndPages.length > 0 && (
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-yellow-400">
                  {linkAnalysis.deadEndPages.length} Dead-End Page{linkAnalysis.deadEndPages.length > 1 ? 's' : ''} (No Outbound Links)
                </p>
                <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                  {linkAnalysis.deadEndPages.slice(0, 5).map((page, i) => (
                    <li key={i}>{page}</li>
                  ))}
                  {linkAnalysis.deadEndPages.length > 5 && (
                    <li className="italic">...and {linkAnalysis.deadEndPages.length - 5} more</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}
        {/* Dead-End Pages context */}
        {linkAnalysis.deadEndPages.length > 0 && (
          <ContextNote text="Visitors on these pages have nowhere to go next. Adding links to your menu, reservations, or events page keeps them engaged." />
        )}
      </section>

      {/* Navigation Structure */}
      {(navigationStructure.mainNavLinks.length > 0 || navigationStructure.footerLinks.length > 0) && (
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <Network className="h-5 w-5 text-muted-foreground" />
            <h4 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
              Navigation Structure
            </h4>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {navigationStructure.mainNavLinks.length > 0 && (
              <div className="bg-secondary/30 rounded-lg p-4">
                <h5 className="text-sm font-medium mb-2">Main Navigation ({navigationStructure.mainNavLinks.length})</h5>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {navigationStructure.mainNavLinks.slice(0, 8).map((link, i) => (
                    <li key={i} className="truncate">{link}</li>
                  ))}
                  {navigationStructure.mainNavLinks.length > 8 && (
                    <li className="italic">...and {navigationStructure.mainNavLinks.length - 8} more</li>
                  )}
                </ul>
              </div>
            )}

            {navigationStructure.footerLinks.length > 0 && (
              <div className="bg-secondary/30 rounded-lg p-4">
                <h5 className="text-sm font-medium mb-2">Footer Links ({navigationStructure.footerLinks.length})</h5>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {navigationStructure.footerLinks.slice(0, 8).map((link, i) => (
                    <li key={i} className="truncate">{link}</li>
                  ))}
                  {navigationStructure.footerLinks.length > 8 && (
                    <li className="italic">...and {navigationStructure.footerLinks.length - 8} more</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Visual Sitemap */}
      {sitemap && (
        <section className="space-y-4">
          <h4 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
            Visual Site Structure
          </h4>
          <div className="bg-secondary/30 rounded-lg p-4">
            <SitemapTree node={sitemap} />
          </div>
        </section>
      )}

      {/* All Issues */}
      {(mediumIssues.length > 0 || lowIssues.length > 0) && (
        <section className="space-y-4">
          <h4 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
            Additional Issues
          </h4>
          <div className="space-y-2">
            {mediumIssues.map((issue, i) => (
              <IssueRow key={`medium-${i}`} issue={issue} />
            ))}
            {lowIssues.map((issue, i) => (
              <IssueRow key={`low-${i}`} issue={issue} />
            ))}
          </div>
        </section>
      )}

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-muted-foreground" />
            <h4 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
              Recommendations
            </h4>
          </div>
          <div className="space-y-2">
            {recommendations.map((rec, i) => (
              <AccordionRecommendation key={i} recommendation={rec} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function StatBox({
  label,
  value,
  status,
}: {
  label: string;
  value: number | string;
  status?: 'good' | 'warning' | 'bad';
}) {
  const statusColors = {
    good: 'text-green-400',
    warning: 'text-yellow-400',
    bad: 'text-red-400',
  };

  return (
    <div className="bg-secondary/30 rounded-lg p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-2xl font-light ${status ? statusColors[status] : ''}`}>{value}</p>
    </div>
  );
}

function IssueRow({ issue }: { issue: SiteArchitecture['issues'][0] }) {
  const severityStyles = {
    critical: 'bg-red-500/10 border-red-500/20 text-red-400',
    high: 'bg-red-500/10 border-red-500/20 text-red-400',
    medium: 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400',
    low: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
  };

  const icons = {
    critical: <XCircle className="h-4 w-4" />,
    high: <XCircle className="h-4 w-4" />,
    medium: <AlertTriangle className="h-4 w-4" />,
    low: <AlertTriangle className="h-4 w-4" />,
  };

  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${severityStyles[issue.severity]}`}>
      <span className="flex-shrink-0 mt-0.5">{icons[issue.severity]}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm">{issue.message}</p>
        {issue.affectedPages && issue.affectedPages.length > 0 && (
          <p className="text-xs text-muted-foreground mt-1">
            Affected: {issue.affectedPages.slice(0, 3).join(', ')}
            {issue.affectedPages.length > 3 && ` (+${issue.affectedPages.length - 3} more)`}
          </p>
        )}
      </div>
    </div>
  );
}

function ContextNote({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2.5 px-4 py-3 bg-secondary/20 rounded-lg border border-border/40">
      <Info className="h-4 w-4 text-muted-foreground/60 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
    </div>
  );
}

function AccordionRecommendation({
  recommendation,
}: {
  recommendation: SiteArchitecture['recommendations'][0];
}) {
  const [expanded, setExpanded] = useState(false);

  const priorityAccents = {
    critical: 'border-l-red-500/60',
    high: 'border-l-orange-500/60',
    medium: 'border-l-yellow-500/60',
    low: 'border-l-blue-500/60',
  };

  const priorityLabels = {
    critical: 'Critical',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
  };

  return (
    <div className={`rounded-lg border border-border/50 overflow-hidden border-l-2 ${priorityAccents[recommendation.priority]}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-secondary/20 transition-colors"
      >
        <ChevronDown className={`h-4 w-4 text-muted-foreground/40 shrink-0 mt-0.5 transition-transform duration-200 ${
          expanded ? 'rotate-180' : ''
        }`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h5 className="text-sm font-medium">{recommendation.title}</h5>
            <span className="text-xs text-muted-foreground/60 uppercase">{priorityLabels[recommendation.priority]}</span>
          </div>
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-200 ${
        expanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="px-4 pb-4 pt-1 ml-7">
          <p className="text-sm text-muted-foreground leading-relaxed">{recommendation.description}</p>
          <p className="text-sm text-foreground/70 mt-2 italic">Impact: {recommendation.impact}</p>
        </div>
      </div>
    </div>
  );
}
